import type { ComponentPropsWithoutRef, JSX } from 'react';
import { isNil } from '@wistia/type-guards';
import { styled, css } from 'styled-components';
import type { IconNameType } from './iconMap';
import { iconMap } from './iconMap';
import { useResponsiveProp } from '../../private/hooks/useResponsiveProp';
import type { ColorSchemeTypes } from '../ColorSchemeWrapper';
import { getColorScheme } from '../../private/helpers/getColorScheme';
import type { ResponsiveObject } from '../../private/hooks/useResponsiveProp/useResponsiveProp';

// size in pixels
export const iconSizeMap = {
  sm: '12',
  md: '16',
  lg: '24',
  xl: '32',
  xxl: '48',
};

type StyledIconProps = {
  $colorScheme?: ColorSchemeTypes;
  $height?: string | undefined;
  $isInline?: boolean;
  $width?: string | undefined;
};

export const iconInlineStyle = css`
  & > [data-wui-icon-inline] {
    --wui-icon-inline-size: 0.8em;
    --wui-icon-inline-offset: 0.05em;
    --wui-icon-vertical-align: none;

    top: var(--wui-icon-inline-offset);
    position: relative;
  }
`;

const StyledIcon = styled.svg<StyledIconProps>`
  ${({ $colorScheme }) => getColorScheme($colorScheme ?? 'inherit')}
  --wui-icon-size: var(--wui-icon-inline-size, ${({ $height }) => $height}px);
  --wui-icon-vertical-align: middle;

  flex: 0 0 var(--wui-icon-size);
  height: var(--wui-icon-size);
  width: var(--wui-icon-size);
`;

export type IconProps = ComponentPropsWithoutRef<'svg'> & {
  /**
   * Sets the [color scheme](../?path=/docs/docs-color-schemes--docs)
   */
  colorScheme?: ColorSchemeTypes;
  /**
   * Determines if the icon should use inline styles when used with [Text]() and [Heading]() components
   */
  useInlineStyles?: boolean;
  /**
   * Is Icon on a filled background?
   */
  invertColor?: boolean;
  /**
   * The size of the icon to display
   * @type
   * ResponsiveObject<'sm' | 'md' | 'lg' | 'xl' | 'xxl'> | 'sm' | 'md' | 'lg' | 'xl' | 'xxl'
   */
  size?: ResponsiveObject<keyof typeof iconSizeMap> | keyof typeof iconSizeMap;
  /**
   * @ignore
   */
  style?: Record<string, unknown>;
  /**
   * Determines which icon to display
   */
  type: IconNameType;
};

/**
 * A component that renders any of our available icons.
 *
 * For inline usage with text, wrap the Icon within a Text component
 * and set inline=true. The icon will automatically align with the text
 * using the typography-specific positioning.
 *
 * Note that icons should not be used directly for interactivity.
 * If you need an interactive element, use IconButton.
 */
export const Icon = ({
  useInlineStyles = true,
  invertColor,
  colorScheme = 'inherit',
  size = 'lg',
  style,
  type,
  ...props
}: IconProps): JSX.Element => {
  const responsiveSize = useResponsiveProp(size);

  if (isNil(type)) {
    throw new Error('An Icon component requires a `type` prop to be provided');
  }

  if (isNil(iconMap[type])) {
    throw new Error(`Type "${type}" does not exist, please update type prop in Icon component.`);
  }

  if (isNil(iconSizeMap[responsiveSize])) {
    throw new Error(
      `Size "${responsiveSize}" does not exist, please update size prop in Icon component.`,
    );
  }

  const IconElement = iconMap[type];

  // so we do not have to add this to each individual Icon but still allow overrides
  const defaultStyle = {
    verticalAlign: 'var(--wui-icon-vertical-align)',
    ...style,
  };

  const iconColor = invertColor ? 'var(--wui-color-icon-on-fill)' : 'var(--wui-color-icon)';

  return (
    <StyledIcon
      $colorScheme={colorScheme}
      $height={iconSizeMap[responsiveSize]}
      $width={iconSizeMap[responsiveSize]}
      aria-hidden="true"
      as={IconElement}
      data-wui-icon-inline={useInlineStyles ? true : undefined}
      role="presentation"
      style={defaultStyle}
      viewBox="0 0 24 24"
      xmlns="http://www.w3.org/2000/svg"
      {...props}
      // guard against `color` being snuck in as a prop since, at this point in time, we don't want to allow color overrides
      color={iconColor}
    />
  );
};

Icon.displayName = 'Icon';
